import { useState, Fragment } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  ArrowRight,
  Calendar,
  Filter,
  Clock,
  TrendingUp,
  Frown,
  Timer,
  ShieldAlert,
  Users,
  CircleX,
  Play,
} from "lucide-react";
import PageSeo from "../components/PageSeo";
import PageTopbar from "../components/PageTopbar";
import YoutubeVideoLightbox from "../components/YoutubeVideoLightbox";
import YoutubeThumbnail from "../components/YoutubeThumbnail";
import { HIRING_MANAGERS_VIDEO_ID } from "../lib/youtubeVideos";
import { buildBreadcrumbNavItems } from "../lib/pageSeo";
import Nd1SiteNav from "./components/Nd1SiteNav";
import Nd1SiteFooter from "./components/Nd1SiteFooter";
import { Nd1GreenTickIcon, Nd1OrangeTickIcon } from "./components/Nd1TickIcons";
import "./nd1.css";

const PROBLEMS = [
  {
    icon: "/images/hiring-problems/ai-applications.png",
    title: "AI generated applications",
    copy: "Applications created or heavily rewritten by AI — hard to tell who you're really talking to.",
  },
  {
    icon: "/images/hiring-problems/keyword-cvs.png",
    title: "Keyword optimised CVs",
    copy: "CVs tuned to pass filters rather than reflect real skills, experience, or motivation.",
  },
  {
    icon: "/images/hiring-problems/fake-profiles.png",
    title: "Fake profiles",
    copy: "Misleading profiles and exaggerated experience that waste time once you dig in.",
  },
  {
    icon: "/images/hiring-problems/buried-candidates.png",
    title: "Genuine candidates get buried",
    copy: "The people you're actually looking for disappear in a pile of polished noise.",
  },
];

const BENEFITS = [
  {
    icon: ShieldCheck,
    title: "Verified genuine candidates",
    copy: "See applicants who've put in the work to prove they're real — not just another polished CV.",
  },
  {
    icon: Filter,
    title: "Higher quality applications",
    copy: "Spend less time wading through irrelevant applications and more time on people who fit.",
  },
  {
    icon: Clock,
    title: "Save time",
    copy: "Cut hours of screening with clearer signals about who's genuinely worth a conversation.",
  },
  {
    icon: TrendingUp,
    title: "Better hiring outcomes",
    copy: "Make faster, more confident decisions with more context and less guesswork.",
  },
];

const HIRE_THROUGH_POINTS = [
  "One job post",
  "Wider reach",
  "One pipeline",
  "Video introductions",
  "Genuine applicant signals",
];

const FILTER_POINTS = [
  "Reduce noise",
  "Prioritise genuine candidates",
  "Better conversations",
  "Faster screening",
  "Better hiring decisions",
];

const TRADITIONAL_ROWS = [
  {
    icon: Frown,
    copy: "Hundreds of low-quality applications",
  },
  {
    icon: Timer,
    copy: "Hours spent screening and filtering",
  },
  {
    icon: ShieldAlert,
    copy: "Hard to detect fake or misleading profiles",
  },
  {
    icon: Users,
    copy: "Great candidates buried in the noise",
  },
  {
    icon: CircleX,
    copy: "Slow hiring and missed opportunities",
  },
];

const JOBUWIN_ROWS = [
  "More relevant, verified applications",
  "Save hours every week",
  "We verify people and their information",
  "Genuine candidates stand out",
  "Faster decisions and better hires",
];

export default function Nd1HiringManagersPage() {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <div className="nd1-root nd1-root--hiring">
      <PageSeo
        title="Hiring managers — focus on genuine applicants"
        description="JOBUWIN helps hiring managers cut through AI noise, fake profiles, and keyword-stuffed CVs — so you can focus on real people and better hires."
        path="/hiring-managers"
        canonicalPath="/hiring-managers"
        schemaType="website"
      />

      <a href="#nd1-main" className="nd1-skip-link">
        Skip to content
      </a>

      <Nd1SiteNav />

      <main id="nd1-main">
        {/* Hero */}
        <section className="nd1-shell nd1-hm-hero">
          <PageTopbar
            breadcrumbsOnly
            breadcrumbItems={buildBreadcrumbNavItems([
              { name: "Home", path: "/" },
              { name: "For hiring managers", path: "/hiring-managers", current: true },
            ])}
          />
          <div className="nd1-hm-hero__grid">
            <div className="nd1-hm-hero__content">
              <span className="nd1-eyebrow-chip">
                <ShieldCheck className="nd1-icon" aria-hidden="true" />
                Verified hiring for real people
              </span>

              <h1 className="nd1-hm-hero__title">
                <span>Don&apos;t waste time on</span>
                <span>applications that</span>
                <span className="nd1-ink-green">aren&apos;t genuine.</span>
              </h1>

              <p className="nd1-hm-hero__copy">
                If you&apos;re responsible for hiring, you know how hard it is to filter through hundreds of
                applications to find the people who are truly right for the role. JOBUWIN helps you focus on
                real people — not just polished CVs.
              </p>

              <div className="nd1-hm-hero__actions">
                <Link to="/signup?type=hiring-manager" className="nd1-btn nd1-btn--accent">
                  Start hiring
                  <ArrowRight className="nd1-icon" aria-hidden="true" />
                </Link>
                <a href="mailto:hello@jobuwin.com" className="nd1-btn nd1-btn--outline">
                  <Calendar className="nd1-icon" aria-hidden="true" />
                  Let&apos;s talk
                </a>
              </div>
            </div>

            <div className="nd1-hm-hero__media">
              <button
                type="button"
                className="nd1-hm-video"
                onClick={() => setVideoOpen(true)}
                aria-label="Play video: CJ explains why you should use JOBUWIN"
              >
                <YoutubeThumbnail
                  videoId={HIRING_MANAGERS_VIDEO_ID}
                  alt="CJ explains why you should use JOBUWIN"
                  loading="eager"
                  fetchPriority="high"
                />
                <span className="nd1-hm-video__play" aria-hidden="true">
                  <Play className="nd1-icon" />
                </span>
              </button>
              <p className="nd1-hm-hero__caption">
                <span className="nd1-hm-hero__caption-arrow" aria-hidden="true">
                  ↳
                </span>
                CJ explains why you should use JOBUWIN
              </p>
            </div>
          </div>
        </section>

        {/* The hiring problem */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">The hiring problem</h2>
            <p className="nd1-hm-section-head__copy">Hiring managers are drowning in noise.</p>
          </div>

          <div className="nd1-hm-problem-grid">
            {PROBLEMS.map((item) => (
              <article key={item.title} className="nd1-card nd1-hm-problem-card">
                <span className="nd1-hm-problem-icon-wrap">
                  <img
                    className="nd1-hm-problem-icon"
                    src={item.icon}
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                </span>
                <h3 className="nd1-hm-problem-card__title">{item.title}</h3>
                <p className="nd1-hm-problem-card__copy">{item.copy}</p>
              </article>
            ))}
          </div>
        </section>

        {/* What hiring managers love */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">What hiring managers love</h2>
            <p className="nd1-hm-section-head__copy">More clarity. Less noise. Better hires.</p>
          </div>

          <div className="nd1-hm-love-grid">
            {BENEFITS.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="nd1-card nd1-hm-love-card">
                  <span className="nd1-hm-icon-badge nd1-hm-icon-badge--green">
                    <Icon className="nd1-icon" aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="nd1-hm-love-card__title">{item.title}</h3>
                    <p className="nd1-hm-love-card__copy">{item.copy}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        {/* How JOBUWIN helps */}
        <section id="how-it-works" className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head nd1-hm-section-head--wide">
            <h2 className="nd1-hm-section-head__title">How JOBUWIN helps</h2>
            <p className="nd1-hm-section-head__copy">
              Whether you&apos;re posting a new role or working through an existing applicant pool, JOBUWIN
              helps you find genuine people faster — with clearer signals and less noise.
            </p>
          </div>

          <div className="nd1-hm-help-grid">
            <article className="nd1-hm-help-card nd1-hm-help-card--orange">
              <h3 className="nd1-hm-help-card__title">
                <span className="nd1-hm-help-card__num">1</span>
                Hire through JOBUWIN
              </h3>
              <div className="nd1-hm-help-card__copy">
                <p>Post your role once.</p>
                <p>
                  We&apos;ll distribute it across popular job channels and bring applicants back to one place
                  where you can manage everything.
                </p>
                <p>
                  Instead of juggling multiple platforms, you&apos;ll have one pipeline and one conversation
                  space.
                </p>
              </div>
              <div className="nd1-hm-help-card__body">
                <div className="nd1-hm-help-card__art">
                  <img
                    className="nd1-hm-help-card__art-img"
                    src="/images/hire-through-jobuwin-channels.png"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                </div>
                <ul className="nd1-check-list nd1-check-list--orange">
                  {HIRE_THROUGH_POINTS.map((item) => (
                    <li key={item} className="nd1-check-list__item">
                      <Nd1OrangeTickIcon className="nd1-icon" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/signup?type=hiring-manager" className="nd1-btn nd1-btn--accent nd1-hm-help-card__cta">
                Start hiring through JOBUWIN
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </article>

            <article className="nd1-hm-help-card nd1-hm-help-card--green">
              <h3 className="nd1-hm-help-card__title">
                <span className="nd1-hm-help-card__num">2</span>
                Already have applicants?
              </h3>
              <div className="nd1-hm-help-card__copy">
                <p>
                  If you&apos;ve already advertised your role and have hundreds of applications, JOBUWIN can
                  help you identify the candidates who deserve your attention.
                </p>
                <p>
                  Instead of relying only on CVs and keywords, collect stronger signals and help genuine
                  people stand out.
                </p>
              </div>
              <div className="nd1-hm-help-card__body">
                <div className="nd1-hm-help-card__art">
                  <img
                    className="nd1-hm-help-card__art-img"
                    src="/images/candidate-filtering.png"
                    alt=""
                    aria-hidden="true"
                    loading="lazy"
                  />
                </div>
                <ul className="nd1-check-list">
                  {FILTER_POINTS.map((item) => (
                    <li key={item} className="nd1-check-list__item">
                      <Nd1GreenTickIcon className="nd1-icon" aria-hidden="true" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <Link to="/signup?type=hiring-manager" className="nd1-btn nd1-btn--primary nd1-hm-help-card__cta">
                See how candidate filtering works
                <ArrowRight className="nd1-icon" aria-hidden="true" />
              </Link>
            </article>
          </div>
        </section>

        {/* Traditional hiring vs JOBUWIN */}
        <section className="nd1-shell nd1-section">
          <div className="nd1-hm-section-head">
            <h2 className="nd1-hm-section-head__title">Traditional hiring vs JOBUWIN</h2>
            <p className="nd1-hm-section-head__copy">See the difference that matters.</p>
          </div>

          <div className="nd1-hm-compare">
            <div className="nd1-hm-compare__table">
              <h3 className="nd1-hm-compare__heading nd1-hm-compare__heading--traditional">
                Traditional hiring
              </h3>
              <h3 className="nd1-hm-compare__heading nd1-hm-compare__heading--jobuwin">With JOBUWIN</h3>
              <div className="nd1-hm-compare__vs" aria-hidden="true">
                VS
              </div>
              {TRADITIONAL_ROWS.map((row, index) => {
                const Icon = row.icon;
                return (
                  <Fragment key={row.copy}>
                    <div className="nd1-hm-compare__cell nd1-hm-compare__cell--traditional">
                      <span className="nd1-hm-compare__icon">
                        <Icon className="nd1-icon" aria-hidden="true" />
                      </span>
                      <span>{row.copy}</span>
                    </div>
                    <div className="nd1-hm-compare__cell nd1-hm-compare__cell--jobuwin">
                      <Nd1GreenTickIcon className="nd1-icon nd1-hm-compare__tick" aria-hidden="true" />
                      <span>{JOBUWIN_ROWS[index]}</span>
                    </div>
                  </Fragment>
                );
              })}
            </div>
          </div>
        </section>
      </main>

      <Nd1SiteFooter />

      <YoutubeVideoLightbox
        open={videoOpen}
        onClose={() => setVideoOpen(false)}
        videoId={HIRING_MANAGERS_VIDEO_ID}
        title="CJ explains why you should use JOBUWIN"
      />
    </div>
  );
}
